import classNames from 'classnames';
import React, {Fragment} from 'react';
import {FormattedMessage} from 'react-intl';
import './Details.less';

export function DetailsItem({id, classname, value}) {
    if (value && value > 0) {
        return (
            <div
                className={classNames(
                    `Details__${classname}`,
                    'text-capitalize',
                    'Details__label'
                )}
                aria-hidden="true"
            >
                <FormattedMessage
                    id={id}
                    description={`${classname} label`}
                    defaultMessage={`${classname}`}
                >
                    {(txt) => (
                        <Fragment>
                            {value} {txt}
                        </Fragment>
                    )}
                </FormattedMessage>
            </div>
        );
    }
    return null;
}

export function BedroomsPropertyTypeElement({bedrooms, propertyType}) {
    if (bedrooms > 0) {
        return (
            <Fragment>
                <div
                    className="Details__propertyType Details__label"
                    aria-hidden="true"
                >
                    {propertyType}
                </div>
                <div
                    className="Details__bedrooms Details__label"
                    aria-hidden="true"
                >
                    <FormattedMessage
                        id="bedroomsAbbreviationParam"
                        description="bedrooms abbreviation label"
                        defaultMessage="BR"
                        values={{
                            bedrooms
                        }}
                    >
                        {(txt) => txt}
                    </FormattedMessage>
                </div>
            </Fragment>
        );
    }
    return (
        <Fragment>
            <div
                className="Details__propertyType Details__label"
                aria-hidden="true"
            >
                {propertyType}
            </div>
        </Fragment>
    );
}

export function HalfBathElement({bathroomsHalf, bathroomsToiletOnly}) {
    if (bathroomsHalf && bathroomsHalf > 0) {
        return (
            <DetailsItem
                id="bathroomsHalfAbbreviation"
                classname="halfbathrooms"
                value={bathroomsHalf}
            />
        );
    } else if (bathroomsToiletOnly && bathroomsToiletOnly > 0) {
        return (
            <DetailsItem
                id="bathroomsToiletOnlyAbbreviation"
                classname="halfbathrooms"
                value={bathroomsToiletOnly}
            />
        );
    }
    return null;
}

export function SleepsElement({value}) {
    if (value && value > 0) {
        return (
            <div className="Details__sleeps Details__label" aria-hidden="true">
                <FormattedMessage
                    id="sleepsAbbreviation"
                    description="sleeps label"
                    defaultMessage="Sleeps {value}"
                    values={{
                        value
                    }}
                >
                    {(txt) => txt}
                </FormattedMessage>
            </div>
        );
    }
    return null;
}

export function BathroomsElement({
    bathroomsFull,
    bathroomsHalf,
    bathroomsToiletOnly
}) {
    if (bathroomsFull && bathroomsFull > 0) {
        return (
            <Fragment>
                <div
                    className="Details__bathrooms Details__label"
                    aria-hidden="true"
                >
                    <FormattedMessage
                        id={'bathroomsFullAbbreviationParam'}
                        description={'bathrooms label'}
                        defaultMessage={'bathrooms'}
                        values={{
                            bathroomsFull
                        }}
                    >
                        {(txt) => txt}
                    </FormattedMessage>
                </div>
                <HalfBathElement
                    bathroomsHalf={bathroomsHalf}
                    bathroomsToiletOnly={bathroomsToiletOnly}
                />
            </Fragment>
        );
    }
    return null;
}

export function ScreenReaderDetails({
    bedrooms,
    propertyType,
    bathroomsFull,
    bathroomsHalf,
    bathroomsToiletOnly,
    sleeps,
    area,
    isMetric
}) {
    return (
        <div className="sr-only">
            <FormattedMessage id="propertyType" defaultMessage="property type">
                {
                    (txt) => (
                        <Fragment>
                            {txt}
                            {propertyType}
                        </Fragment>
                    )}
            </FormattedMessage>

            {!!bedrooms && (
                <FormattedMessage
                    id="bedroomsFilterLabel"
                    defaultMessage="bedrooms"
                    values={{bedroomsCount: bedrooms}}
                >
                    {
                        (txt) => (
                            <Fragment>
                                {bedrooms}
                                {txt}
                            </Fragment>
                        )}
                </FormattedMessage>
            )}

            {!!bathroomsFull && (
                <FormattedMessage
                    id="bathroomsFilterLabel"
                    defaultMessage="bathrooms"
                    values={{bathroomsCount: bathroomsFull}}
                >
                    {
                        (txt) => (
                            <Fragment>
                                {bathroomsFull}
                                {txt}
                            </Fragment>
                        )}
                </FormattedMessage>
            )}

            {!!bathroomsHalf && (
                <FormattedMessage
                    id="bathroomsHalfLabel"
                    defaultMessage="half bathroom"
                >
                    {
                        (txt) => (
                            <Fragment>
                                {bathroomsHalf}
                                {txt}
                            </Fragment>
                        )}
                </FormattedMessage>
            )}

            {!!bathroomsToiletOnly && (
                <FormattedMessage
                    id="bathroomsToiletOnlyLabel"
                    defaultMessage="toilet only"
                >
                    {
                        (txt) => (
                            <Fragment>
                                {bathroomsToiletOnly}
                                {txt}
                            </Fragment>
                        )}
                </FormattedMessage>
            )}

            {!!sleeps && (
                <FormattedMessage
                    id="sleepsAbbreviation"
                    defaultMessage="Sleeps {value}"
                    values={{
                        value: sleeps
                    }}
                >
                    {
                        (txt) => txt}
                </FormattedMessage>
            )}

            {!!area && (
                <FormattedMessage
                    id="areaLabelVerbose"
                    defaultMessage={isMetric ? 'Meters' : 'Square Feet'}
                    values={{METRIC: isMetric}}
                >
                    {
                        (txt) => (
                            <Fragment>
                                {area}
                                {txt}
                            </Fragment>
                        )}
                </FormattedMessage>
            )}
        </div>
    );
}

export default function Details({
    listing: {bathrooms, bedrooms, propertyType, sleeps, spaces},
    isMetric
}) {
    const {
        half: bathroomsHalf,
        full: bathroomsFull,
        toiletOnly: bathroomsToiletOnly
    } = bathrooms || {};

    const areaValue =
        spaces &&
        spaces.spacesSummary &&
        spaces.spacesSummary.area &&
        spaces.spacesSummary.area.areaValue;
    return (
        <Fragment>
            <BedroomsPropertyTypeElement
                bedrooms={bedrooms}
                propertyType={propertyType}
            />
            <BathroomsElement
                bathroomsFull={bathroomsFull}
                bathroomsHalf={bathroomsHalf}
                bathroomsToiletOnly={bathroomsToiletOnly}
            />
            <SleepsElement value={sleeps} />
            {!!(areaValue && areaValue > 0) && (
                <div className="Details__label" aria-hidden="true">
                    {`${areaValue} `}
                    <FormattedMessage
                        id="areaLabel"
                        description="units for area"
                        defaultMessage={'Sq. Ft.'}
                        values={{METRIC: isMetric}}
                    >
                        {
                            (formattedMessageText) => formattedMessageText}
                    </FormattedMessage>
                </div>
            )}
            <ScreenReaderDetails
                bedrooms={bedrooms}
                propertyType={propertyType}
                bathroomsFull={bathroomsFull}
                bathroomsHalf={bathroomsHalf}
                bathroomsToiletOnly={bathroomsToiletOnly}
                sleeps={sleeps}
                area={areaValue}
                isMetric={isMetric}
            />
        </Fragment>
    );
}
